import { t } from '@lingui/macro';
import dayjs from 'dayjs';
import {
    Dispatch,
    FC,
    SetStateAction,
    memo,
    useContext,
    useState,
} from 'react';
import { toast } from 'sonner';

import { AppButton } from '@/UI';
import { encryptDataWithRSA } from '@/Utils/cryptoUtils';
import { generateUserSign } from '@/Utils/socket-actions/p2p-connection/P2PHelper';
import {
    AddUsersToP2PWhiteList,
    rejectUsersFromP2PWhiteList,
} from '@/Utils/socket-actions/p2p-connection/p2pApis';
import { sendP2PSocketData } from '@/Utils/socket-actions/p2p-connection/p2pConnection';
import { useAppDispatch, useAppSelector } from '@/hooks/useRedux';
import { p2pActions } from '@/store/p2p-slice';
import { IWaitingListItem, UserWhiteListType } from '@/types/key-exchange';

import classes from './P2PAdminPanel.module.css';
import { P2PContext } from './P2PContext';

type IProps = {
    selectedUsers: string[];
    setSelectedUsers: Dispatch<SetStateAction<string[]>>;
    filteredList: (IWaitingListItem | UserWhiteListType)[];
};
const P2PMultipleSelectionActions: FC<IProps> = props => {
    const { selectedUsers, setSelectedUsers, filteredList } = props;
    const utcISOString = dayjs().utc().toISOString();

    const [isSubmitting, setIsSubmitting] = useState(false);

    const context = useContext(P2PContext);
    const { waitingUsersList, setActiveUserItem } = context;

    const serverDataSelector = useAppSelector(state => state.serverDataReducer);
    const activeServer = serverDataSelector?.activeServer;
    const publicKey = serverDataSelector?.serverData?.[activeServer]?.publicKey;

    const selectAll = () => {
        if (selectedUsers.length === filteredList.length) {
            setSelectedUsers([]);
        } else {
            setSelectedUsers(
                (filteredList as IWaitingListItem[]).map(u => u.device_id),
            );
        }
    };

    const dispatch = useAppDispatch();

    const approveUsers = async (list: string[], reject: boolean = false) => {
        setIsSubmitting(true);

        let body = [];

        for (let i = 0; i <= list.length - 1; i++) {
            const targetAccount = waitingUsersList.find(
                el => el.device_id === list[i],
            );

            const userHash = generateUserSign({
                uuid: targetAccount?.device_id,
                userId: targetAccount?.user_id,
            });

            const dateEncrypted = await encryptDataWithRSA(
                utcISOString,
                publicKey,
            );
            const deviceIdEncrypted = await encryptDataWithRSA(
                targetAccount?.device_id,
                publicKey,
            );
            const userHashEncrypted = await encryptDataWithRSA(
                userHash,
                publicKey,
            );
            body.push({
                device_signed: deviceIdEncrypted,
                hash_signed: userHashEncrypted,
                valid_until_signed: dateEncrypted,
                user_id: targetAccount?.user_id,
                chat_subserver_id: activeServer,
            });
        }

        const func = reject
            ? rejectUsersFromP2PWhiteList
            : AddUsersToP2PWhiteList;
        const successMsg = reject
            ? t`Users added to the rejected`
            : t`Users added to the permitted whitelist`;

        const errorMsg = reject
            ? t`Error while rejecting users from the whitelist. Please try again.`
            : t`Error adding users to the permitted whitelist. Please try again.`;

        try {
            const { error, response = undefined } = await func(body);

            if (response && !error) {
                for (let i = 0; i <= list.length - 1; i++) {
                    const targetAccount = waitingUsersList.find(
                        el => el.device_id === list[i],
                    );

                    sendP2PSocketData({
                        command: 'broadcast',
                        data: {
                            event: 'whitelist_user_added',
                            user_id: targetAccount?.user_id,
                            device_id: targetAccount?.device_id,
                        },
                    });
                    setActiveUserItem(null);
                    dispatch(
                        p2pActions.removeWaitingUser(targetAccount?.device_id),
                    );
                }
                toast.success(successMsg);
                setSelectedUsers([]);
            } else {
                toast.error(response || errorMsg);
            }
        } catch (err) {
            console.error(err);
            toast.error(errorMsg);
        } finally {
            setIsSubmitting(false);
        }
    };

    const actionsAll = selectedUsers.length > 0;
    return (
        <div className={classes['bulk-actions']}>
            <AppButton
                style={{ gridColumn: '1/-1' }}
                title={
                    selectedUsers.length > 0
                        ? t`Unselect All` + ` (${selectedUsers.length})`
                        : t`Select All`
                }
                type="button"
                classStyles={classes['btn']}
                onClick={selectAll}
                disable={isSubmitting}
                size="small"
                buttonType={'whiteWithBorder'}
            />

            <AppButton
                title={t`Accept all`}
                type="button"
                classStyles={classes['btn']}
                disable={!actionsAll || isSubmitting}
                onClick={() => approveUsers(selectedUsers, false)}
                size="small"
            />
            <AppButton
                title={t`Reject All`}
                type="button"
                classStyles={classes['btn']}
                onClick={() => approveUsers(selectedUsers, true)}
                buttonType={'secondary'}
                size="small"
                disable={!actionsAll || isSubmitting}
            />
        </div>
    );
};
export default memo(P2PMultipleSelectionActions);
